import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Modal } from "react-bootstrap";
import { MultiCheckDropdown } from "Components/MultiCheckDropdown";
import { ButtonLoader } from "Components/ButtonLoader";
import { authService } from "Service/Axios";
import {
  CREATE_PROJECT_ATTRIBUTE_URL,
  GET_ADMIN_PROJECT_STAGE_LIST_URL,
  UPDATE_PROJECT_ATTRIBUTE_URL,
} from "Constant/api";
import { Validator } from "Utils/validator";

export const CreateAttribute = ({
  show,
  isCopy,
  toggle,
  selectedData,
  fetchList,
  projectList,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [stageList, setStageList] = useState([]);
  const [formData, setFormData] = useState({
    projectId: "",
    isStage: "yes",
    stageIds: [],
    attributeName: "",
  });
  const [errMsg, setErrMsg] = useState({
    projectId: "",
    attributeName: "",
    stageIds: "",
  });

  const { projectId, isStage, stageIds, attributeName } = formData;

  // On Change
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    if (e.target.name === "projectId") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          projectId: t("common.messages.error.adminSettings.projectId.blank"),
        }));
      } else {
        fetchStageListByProject(e.target.value);
        setErrMsg((prev) => ({
          ...prev,
          projectId: "",
        }));
      }
    }

    if (e.target.name === "isStage") {
      setFormData((prev) => ({ ...prev, stageIds: [] }));
    }

    if (e.target.name === "attributeName") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          attributeName: t(
            "common.messages.error.adminSettings.attributeName.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          attributeName: "",
        }));
      }
    }
  };

  const handleChangeMultiple = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected,
    }));

    if (name === "stageIds") {
      if (!Validator.hasMultiple(selected)) {
        setErrMsg((prev) => ({
          ...prev,
          stageIds: t("common.messages.error.adminSettings.stageIds.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          stageIds: "",
        }));
      }
    }
  };

  // Listing
  const fetchStageListByProject = async (projectId) => {
    try {
      const payload = { projectId };
      const response = await authService(
        GET_ADMIN_PROJECT_STAGE_LIST_URL,
        "POST",
        payload
      );
      if (response) {
        setStageList(response);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  const modifiedStageList = useMemo(() => {
    return stageList?.length > 0
      ? stageList.map((res) => ({
          label: res.name,
          value: res.gid,
        }))
      : [];
  }, [stageList]);

  useEffect(() => {
    if (selectedData) {
      fetchStageListByProject(selectedData.projectId);
      setFormData((prev) => ({
        ...prev,
        projectId: selectedData.projectId,
        isStage: selectedData.stageAvailable ? "yes" : "no",
        attributeName: selectedData.name,
      }));
    }
  }, [selectedData]);

  useEffect(() => {
    if (selectedData && selectedData.stages?.length > 0) {
      setFormData((prev) => ({
        ...prev,
        stageIds: modifiedStageList?.filter((obj) =>
          selectedData.stages?.includes(obj.value)
        ),
      }));
    }
  }, [selectedData, modifiedStageList]);

  const getValidation = () => {
    return new Promise((resolve) => {
      if (Validator.text(projectId) && Validator.text(attributeName)) {
        if (isStage !== "no") {
          if (Validator.hasMultiple(stageIds)) {
            resolve(true);
          } else {
            setErrMsg((prev) => ({
              ...prev,
              stageIds: t("common.messages.error.adminSettings.stageIds.blank"),
            }));
            resolve(false);
          }
        } else {
          resolve(true);
        }
      } else {
        if (!Validator.text(projectId)) {
          setErrMsg((prev) => ({
            ...prev,
            projectId: t("common.messages.error.adminSettings.projectId.blank"),
          }));
        }

        if (attributeName && !Validator.text(attributeName)) {
          setErrMsg((prev) => ({
            ...prev,
            attributeName: t(
              "common.messages.error.adminSettings.attributeName.blank"
            ),
          }));
        }
        resolve(false);
      }
    });
  };

  // On Submit
  const onSaveAttribute = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        const payload = {
          projectId,
          name: attributeName,
          stageAvailable: isStage === "yes",
        };

        if (isStage === "yes") {
          payload.stages = stageIds?.map((option) => option.value);
        }

        if (isCopy) {
          payload.copy = true;
          payload.copyId = selectedData.gid;
        }

        if (selectedData && !isCopy) {
          payload.gid = selectedData.gid;
          const response = await authService(
            UPDATE_PROJECT_ATTRIBUTE_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(
              t("common.messages.notification.success.updateAttribute")
            );
          } else {
            toast.error(response.message);
          }
        } else {
          const response = await authService(
            CREATE_PROJECT_ATTRIBUTE_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(
              t(
                `common.messages.notification.success.${
                  isCopy ? "copyAttribute" : "createAttribute"
                }`
              )
            );
          } else {
            toast.error(response.message);
          }
        }
        toggle();
        fetchList();
        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (error.response && error.response.data) {
          toast.error(error.response.data.message);
        }
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={toggle}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="justify-content-center">
        <Modal.Title>
          {t(
            `auth.adminSettings.${isCopy ? "copyAttribute" : "createAttribute"}`
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={`mb-3 ${errMsg.projectId ? "error" : ""}`}>
          <label htmlFor="projectId" className="form-label">
            {t("common.label.project")}
          </label>
          <select
            className="form-select custom-select"
            aria-label="Default select example"
            id="projectId"
            name="projectId"
            value={formData.projectId}
            onChange={handleChange}
            disabled={selectedData}
          >
            <option> {t("common.label.select")}</option>
            {projectList &&
              projectList.length > 0 &&
              projectList.map((project) => (
                <option value={project.value} key={project.value}>
                  {project.label}
                </option>
              ))}
          </select>
          {errMsg.projectId && <p>{errMsg.projectId}</p>}
        </div>
        <div className="row">
          <div className="col-12">
            <div className="mb-1">
              <label htmlFor="isStage" className="form-label me-3">
                Stage Required
              </label>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  id="isStage"
                  value="yes"
                  checked={isStage === "yes"}
                  name="isStage"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.yes")}
                </label>
              </div>
              <div className="form-check form-check-inline">
                <label htmlFor="isStage" className="form-check-label"></label>
                <input
                  type="radio"
                  id="isStage"
                  value="no"
                  checked={isStage === "no"}
                  name="isStage"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.no")}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className={`mb-3 ${errMsg.stageIds ? "error" : ""}`}>
          <label htmlFor="stageIds" className="form-label">
            {t("common.label.stages")}
          </label>
          <MultiCheckDropdown
            placeholder={t("common.placeHolder.stages")}
            options={modifiedStageList}
            value={stageIds}
            onChange={(selected) => handleChangeMultiple(selected, "stageIds")}
            disabled={projectId === "" || isStage === "no"}
          />
          {errMsg.stageIds && <p>{errMsg.stageIds}</p>}
        </div>
        <div className={`mb-3 ${errMsg.attributeName ? "error" : ""}`}>
          <label htmlFor="attributeName" className="form-label">
            {t("common.label.attribute")}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder={t("common.placeHolder.attributeName")}
            id="attributeName"
            name="attributeName"
            value={attributeName}
            onChange={handleChange}
          />
          {errMsg.attributeName && <p>{errMsg.attributeName}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          {t("common.button.close")}
        </Button>
        {!loading ? (
          <Button variant="primary" onClick={onSaveAttribute}>
            {t("common.button.save")}
          </Button>
        ) : (
          <ButtonLoader className="btn-loading-sm" />
        )}
      </Modal.Footer>
    </Modal>
  );
};
